<!--
  The Review view's conversation: everything said in review so far, by
  every reviewer and the author (logic/reviewConversation), and the place
  to answer it. Open threads come first, each folded to a line until the
  reader unfolds it (remembered per pull request); unfolded, a thread shows
  the diff lines it was written on and is the same thread the Files view
  shows, with Reply, Done, Ack and Resolve. A reply, Done or Ack written
  here is a draft like any other: it joins the unsent drafts above, not
  ticked. Reviews and conversation comments are the Overview's own cards.
  Commits, pushes and other events are the Overview's timeline, not this.
-->
<script lang="ts">
  import type { PrStore } from '../state/pr.svelte';
  import { nav } from '../nav.svelte';
  import { prPath } from '../router';
  import { carryFilter } from '../logic/filefilter';
  import { commentHref, hunkTail, HUNK_ROWS, threadPlace } from '../logic/reviewComments';
  import { conversationEntries, filterCounts, FILTERS, listed, startFilter, threadSummary, type ConversationFilter } from '../logic/reviewConversation';
  import { relTime } from '../logic/time';
  import { session } from '../state/session.svelte';
  import ConversationComment from './ConversationComment.svelte';
  import HunkRows from './HunkRows.svelte';
  import ReviewEntry from './ReviewEntry.svelte';
  import Thread from './Thread.svelte';

  interface Props {
    pr: PrStore;
  }
  let { pr }: Props = $props();

  const me = $derived(session.user?.login ?? pr.viewer);
  const entries = $derived(conversationEntries(pr.timeline, pr.threads, me));
  const counts = $derived(filterCounts(entries));
  /** The filter the reader chose last, else the one the conversation opens on. */
  const filter = $derived<ConversationFilter>(pr.prefs.data.conversationFilter ?? startFilter(entries));
  const shown = $derived(listed(entries, filter));
  const head = $derived(pr.pr?.headRefOid ?? '');
  const ref = $derived({ owner: pr.owner, repo: pr.repo, number: pr.number });
  const unfolded = (id: string) => !!pr.prefs.data.openThreads?.[id];
  const published = (c: { pullRequestReview?: { state: string } | null }) => c.pullRequestReview?.state !== 'PENDING';

  const EMPTY: Record<ConversationFilter, string> = {
    open: 'No open threads: every conversation in this review is resolved.',
    mine: 'You have not reviewed or commented here yet.',
    all: 'Nothing has been said in review yet.',
  };
</script>

<section class="review-part conversation" aria-labelledby="conv-title">
  <div class="part-head">
    <h3 id="conv-title">Conversation</h3>
    <div class="conv-filter" role="group" aria-label="Show">
      {#each FILTERS as f (f.id)}
        <button type="button" class="conv-pick" aria-pressed={filter === f.id} title={f.title} onclick={() => pr.prefs.setConversationFilter(f.id)}
          >{f.label} <span class="conv-n">{counts[f.id]}</span></button
        >
      {/each}
    </div>
  </div>
  <p class="part-note">
    What reviewers and the author have said in review. A reply, Done or Ack you write here is a draft: it joins Unsent above, not ticked. Commits and pushes
    are in the <a href={carryFilter(prPath(pr.owner, pr.repo, pr.number), nav.query)}>Overview</a>.
  </p>
  {#if pr.timelineTruncated}
    <p class="part-note">Older reviews and comments are not loaded here; open threads are all listed.</p>
  {/if}

  {#if shown.length}
    <ol class="conv-list" aria-label="{FILTERS.find((f) => f.id === filter)?.label} review conversation">
      {#each shown as e (e.kind + ':' + e.id)}
        {#if e.kind === 'thread'}
          {@const t = e.thread}
          {@const said = t.comments.nodes.filter(published)}
          {@const sum = threadSummary(t)}
          {@const open = unfolded(t.id)}
          {@const drafts = t.comments.nodes.length - said.length}
          <li class="conv-item conv-thread" class:unfolded={open} data-thread={t.id}>
            <div class="conv-row">
              <button
                type="button"
                class="conv-toggle"
                aria-expanded={open}
                aria-controls={open ? `conv-${t.id}` : undefined}
                onclick={() => pr.prefs.setThreadOpen(t.id, !open)}
              >
                <span class="conv-place mono">{threadPlace(t)}</span>
                <span class="conv-sum"><b>{sum.author}</b>{sum.text ? ': ' + sum.text : ''}</span>
              </button>
              <span class="conv-meta">
                {#if t.isOutdated}<span class="pill pending">outdated</span>{/if}
                {#if t.isResolved}<span class="pill">resolved</span>{/if}
                {#if drafts}<span class="pill neutral" title="Your unsent {drafts === 1 ? 'reply is' : 'replies are'} listed under Unsent, to tick"
                    >{drafts} unsent {drafts === 1 ? 'reply' : 'replies'}</span
                  >{/if}
                <span class="muted">{sum.replies ? `${sum.replies} ${sum.replies === 1 ? 'reply' : 'replies'} · ` : ''}{relTime(said.at(-1)?.createdAt ?? e.at)}</span>
                <a class="conv-open" href={commentHref(t, said[0].id, ref, head, nav.query)} title="Open this thread in the Files view">Open in Files <span aria-hidden="true">→</span></a>
              </span>
            </div>
            {#if open}
              <div class="conv-body" id="conv-{t.id}">
                <HunkRows rows={hunkTail((said.find((c) => c.diffHunk) ?? said[0])?.diffHunk, HUNK_ROWS)} place={threadPlace(t)} />
                <Thread {pr} page={pr} thread={t} />
              </div>
            {/if}
          </li>
        {:else if e.kind === 'review'}
          <li class="conv-item tl-card review-{e.review.state.toLowerCase()}" data-review={e.id}><ReviewEntry {pr} review={e.review} /></li>
        {:else}
          <li class="conv-item tl-card" data-comment={e.id}><ConversationComment {pr} comment={e.comment} /></li>
        {/if}
      {/each}
    </ol>
  {:else}
    <p class="conv-empty muted">{EMPTY[filter]}</p>
  {/if}
</section>
